import ExcelJS from "exceljs";
import papa from "papaparse";

import * as Api from "../../Api";
import LogoGold from "../../assets/logo.png";
import FiveKeysLogo from "../../assets/five-keys-logo.png";
import * as Utils from "./Utils";
import dayjs from "../vendor/DayJs";
import * as DateUtils from "./DateUtils";
import { DataTableColumn } from "../../components/data/DataTable/DataTableTypes";
import { DateRange } from "../../lib/types/DateTimeTypes";
import { isFiveKeysTheme } from "./ThemeUtils";

// export type export type hehe
export type ExportType = "image" | "pdf" | "ppt" | "xlsx" | "csv";

export const pdfLoadableElements: (() => Promise<void> | void)[] = [];

export const DEFAULT_FOOTER = `<div style="width:100%; padding: 0 20px;">
  <img src="${
		isFiveKeysTheme() ? FiveKeysLogo : LogoGold
	}" height="16" style="display: inline-block"/>
  <span style="float:right; color: #999; font-size: 8px; margin-top: 5px; font-family: Barlow, Arial, sans-serif;">
    Report exported on <span class="date"/>
  </span>
</div>`;

const createCSVFile = (content: string) =>
	new Blob(["\uFEFF" + content], { type: "text/csv; charset=utf-8" });

export const downloadCSVFromString = async (name: string, content: string) => {
	const csv = createCSVFile(content);
	downloadBlob(csv, name ? name + ".csv" : "file.csv");
};

const prepRowsFromObjects = (rows: any[], mapper?: (value: any) => any) => {
	const keySet = new Set();
	const items = [];
	for (const row of rows) {
		const item = mapper ? mapper(row) : row;
		items.push(item);
		for (const key of Object.keys(item)) {
			keySet.add(key);
		}
	}
	const keys = Array.from(keySet);

	return [keys, items];
};

const buildCSVStringFromObjects = (
	rows: any[],
	mapper?: (value: any) => any,
) => {
	const [keys, items] = prepRowsFromObjects(rows, mapper);

	return (
		keys.join(",") +
		items.reduce((lines, item) => {
			return (
				lines +
				"\r\n" +
				keys.reduce((line, key) => {
					let value = (item as any)[key as any];
					if (value === null || value === undefined) {
						value = "";
					}
					return `${line}"${value}",`;
				}, "")
			);
		}, "")
	);
};

export const downloadCSVFromObjects = async (
	name: string,
	rows: any[],
	mapper?: (value: any) => any,
) => downloadCSVFromString(name, buildCSVStringFromObjects(rows, mapper));

export const downloadCSVFromRows = async (
	name: string,
	rows: any[],
	delimiter: string = ",",
) => {
	let csvContent = "";
	rows.forEach((rowArray, index) => {
		const row = rowArray
			.map((value: any) => {
				let parsedValue = value;
				if (value === null || value === undefined) {
					parsedValue = "";
				} else if (typeof value !== "string") {
					parsedValue = JSON.stringify(value);
				}
				return `"${parsedValue.replace(/[\n\r]+/g, " ").replace(/"/g, "'")}"`;
			})
			.join(delimiter);
		csvContent += row + "\r\n";
	});
	downloadCSVFromString(name, csvContent);
};

const buildXLSXJSONFromObjects = (
	rows: any[],
	mapper?: (value: any) => any,
) => {
	const [keys, items] = prepRowsFromObjects(rows, mapper);

	const formattedItems = items.reduce((lines, item) => {
		const keyItems = keys.reduce((line: any[], key) => {
			let value = (item as any)[key as any];

			if (value === null || value === undefined) {
				value = "";
			}

			line.push(value);
			return line;
		}, []);

		lines.push(keyItems);
		return lines;
	}, []);

	return [keys, ...formattedItems];
};

export const downloadXLSXFromJSON = async (
	name: string,
	data: { [sheetName: string]: Array<any[]> },
	callback?: (ws: ExcelJS.Worksheet, sheetData: any[]) => void,
) => {
	const wb = new ExcelJS.Workbook();

	Object.keys(data).forEach((sheetName) => {
		const sheetData = data[sheetName];
		const ws = wb.addWorksheet(sheetName);
		const colWidths: any[] = [];

		sheetData.forEach((dataItem) => {
			dataItem.forEach((itemCol, index) => {
				if (!colWidths[index] || itemCol.length > colWidths[index]) {
					colWidths[index] = Math.max(itemCol.length, 5);
				}
			});
		});

		const cols = colWidths.map((colWIdth) => ({ width: colWIdth + 2 }));
		ws.columns = cols;
		ws.addRows(sheetData);

		if (callback) {
			callback(ws, sheetData);
		}
	});

	const buffer = await wb.xlsx.writeBuffer();
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,",
	});

	downloadBlob(blob, name);
};

export const downloadXLSXFromCSVString = async (name: string, data: string) => {
	const sheetData = papa.parse(data);
	return downloadXLSXFromJSON(name, { "Sheet 1": sheetData.data as any[][] });
};

export const downloadXLSXFromObjects = async (
	name: string,
	rows: any[],
	mapper?: (value: any) => any,
) => {
	const xlsxJSON = buildXLSXJSONFromObjects(rows, mapper);
	return downloadXLSXFromJSON(name, { Sheet: xlsxJSON });
};

const downloadPage = async (
	name: string,
	fileType: "pdf" | "png",
	endpoint: any,
	selector?: string,
	options?: any,
) => {
	await Promise.all(pdfLoadableElements.map((func) => func()));
	await new Promise((resolve) => setTimeout(resolve, 100));

	const clonedDocument = document.documentElement.cloneNode(
		true,
	) as HTMLElement;

	const body = clonedDocument.querySelector("body");

	if (body) {
		if (selector) {
			body.innerHTML = clonedDocument.querySelector(selector)?.outerHTML || "";
		}
		body.classList.add("--pdf");

		if (typeof options?.bodyHeight === "number") {
			body.style.height = `${options.bodyHeight}px`;
		}
	}
	clonedDocument
		.querySelectorAll(".--pdf-ignore")
		.forEach((element) => element.remove());
	clonedDocument
		.querySelectorAll("noscript")
		.forEach((element) => element.remove());
	clonedDocument
		.querySelectorAll("a")
		.forEach((element) => Utils.changeTag(element, "div"));
	clonedDocument
		.querySelectorAll(".react-auto-height")
		.forEach((element) => ((element as HTMLElement).style.height = ""));
	clonedDocument
		.querySelectorAll(".Accordion")
		.forEach((element) => element.classList.add("--open"));
	clonedDocument
		.querySelectorAll(".Accordion__content")
		.forEach((element) => ((element as HTMLElement).style.height = ""));

	const { header, footer } = options || {};

	const response = await endpoint.submit({
		body: {
			html: clonedDocument.outerHTML,
			header: header || undefined,
			footer: footer === undefined ? DEFAULT_FOOTER : footer,
		},
	});

	const mimeType = fileType === "pdf" ? "application/pdf" : "image/png";
	const blob = new Blob([response.data], { type: mimeType });

	if (options?.noDownload) {
		return blob;
	}

	return downloadBlob(blob, name);
};

export const downloadPageAsPDF = async (
	name: string,
	selector?: string,
	options?: any,
) => {
	return downloadPage(
		`${name || "page"}.pdf`,
		"pdf",
		Api.postPdfConverter,
		selector,
		options,
	);
};

export const downloadPageAsImage = async (
	name: string,
	selector?: string,
	options?: any,
) => {
	return downloadPage(
		`${name || "page"}.png`,
		"png",
		Api.postImageConverter,
		selector,
		options,
	);
};

export const downloadBlob = (blob: Blob, name: string, saveAs?: boolean) => {
	const navigator = window.navigator as any;
	if (navigator.msSaveOrOpenBlob) {
		navigator.msSaveOrOpenBlob(blob);
	} else {
		const objectUrl = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.setAttribute("href", objectUrl);
		link.setAttribute("download", name);
		link.click();
	}
};

export const downloadMileageReport = async (
	userId: string,
	name?: string,
	from?: number,
	to?: number,
) => {
	const response = await Api.fetchMileageExportDownload.submit({
		params: { userId: userId },
		query: { from, to },
		options: { cache: false },
	});
	const blob = new Blob([response.data], {
		type: "application/vnd.ms-excel",
	});
	downloadBlob(blob, name || `report_${userId}.xlsx`);
};

export const downloadTableData = (
	exportName: string,
	allData: any[],
	columns: DataTableColumn[],
	delimiter?: string, // either ; (if turkish) or , (all else)
) => {
	if (allData.length > 0) {
		const rows: any[] = [
			Object.keys(allData[0]).reduce((row: any, key) => {
				if (!key.startsWith("_")) {
					const column = columns.find((column: any) => column.id === key);
					row[key] = column?.Header;
				}
				return row;
			}, {}),
		];
		allData.forEach((obj: any) => {
			const keys = Object.keys(obj);
			rows.push(
				keys.reduce((row: any, key, index) => {
					if (!key.startsWith("_")) {
						const column = columns.find((column: any) => column.id === key);
						row[key] = obj[key];
						if (column?.type === "datetime") {
							const formatted = dayjs(obj[key]);
							row[key] = formatted.isValid() ? formatted.format("lll") : "";
						} else if (column?.type === "duration") {
							row[key] = DateUtils.formatDuration({
								value: obj[key],
								format: "h[h] mm[m]",
							});
						}
					}
					return row;
				}, {}),
			);
		});
		downloadCSVFromRows(exportName, rows.map(Object.values), delimiter);
	}
};

export const getExportName = (prefix: string): string => {
	const name = `${prefix.trim()} - ${dayjs().format("ll")}`;
	return name.replace(/\./g, "");
};

export const getReportExportName = (
	prefix: string,
	range: DateRange | undefined,
	period: string,
): string => {
	const name = `${prefix.trim()} - ${
		period === "month"
			? dayjs(range?.from).startOf("month").format("ll")
			: dayjs(range?.from).format("ll")
	} - ${
		period === "month"
			? dayjs(range?.to).endOf("month").format("ll")
			: dayjs(range?.to).format("ll")
	}`;
	return name.replace(/\./g, "");
};
